import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  actionGetBillingHistory,
  actionGetInvoicePdf,
} from "../store/actions/subscriptionAction";

// ─── Skeleton ────────────────────────────────────────────────────────────────

const skeletonStyle = `
  @keyframes bh-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  .bh-skel {
    border-radius: 6px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 50%, #f0f0f0 75%);
    background-size: 600px 100%;
    animation: bh-shimmer 1.4s infinite linear;
    display: block;
  }
`;

const Skel = ({ w, h = 14, rounded = false }) => (
  <span className="bh-skel" style={{ width: w, height: h, borderRadius: rounded ? 999 : 6, display: "block" }} />
);

const SkeletonRows = () =>
  [1, 2, 3, 4, 5].map((i) => (
    <tr key={i} style={{ borderBottom: "1px solid #f4f5f7" }}>
      <td className="ps-4 py-3"><Skel w={88} /></td>
      <td><Skel w={200} /></td>
      <td><Skel w={72} /></td>
      <td><Skel w={60} h={22} rounded /></td>
      <td className="pe-4"><Skel w={96} h={30} rounded /></td>
    </tr>
  ));

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_MAP = {
  completed: { label: "Paid",    cls: "bg-success" },
  active:    { label: "Active",  cls: "bg-success" },
  pending:   { label: "Pending", cls: "bg-warning text-dark" },
  failed:    { label: "Failed",  cls: "bg-danger" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_MAP[status] ?? { label: status, cls: "bg-secondary" };
  return <span className={`badge ${cfg.cls}`} style={{ fontSize: 11 }}>{cfg.label}</span>;
};

// ─── Invoice button ───────────────────────────────────────────────────────────

const InvoiceButton = ({ row, actionGetInvoicePdf }) => {
  const [loading, setLoading] = useState(false);

  const hostedUrl = row.metadata?.hosted_invoice_url;
  const invoicePdf = row.metadata?.invoice_pdf;
  const invoiceId  = row.metadata?.invoice_id;

  const handleDownload = async () => {
    // Use stored PDF URL if available
    if (invoicePdf) { window.open(invoicePdf, "_blank"); return; }
    // Use stored hosted URL if available
    if (hostedUrl) { window.open(hostedUrl, "_blank"); return; }
    // Fallback: fetch from Stripe
    if (!invoiceId) return;
    setLoading(true);
    try {
      const data = await actionGetInvoicePdf(invoiceId);
      const url = data?.invoice_pdf || data?.hosted_invoice_url;
      if (url) window.open(url, "_blank");
    } finally {
      setLoading(false);
    }
  };

  if (!hostedUrl && !invoicePdf && !invoiceId) return <span className="text-muted" style={{ fontSize: 12 }}>—</span>;

  return (
    <button
      className="btn btn-sm btn-outline-primary"
      style={{ borderRadius: 8, fontSize: 12, fontWeight: 600, padding: "4px 12px" }}
      onClick={handleDownload}
      disabled={loading}
      title="Download Invoice"
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm" />
      ) : (
        <><i className="bx bx-download me-1" />Invoice</>
      )}
    </button>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TransactionHistory = ({ actionGetBillingHistory, actionGetInvoicePdf }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]     = useState(0);
  const LIMIT = 20;

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const data = await actionGetBillingHistory(p, LIMIT);
      setRows(Array.isArray(data?.data) ? data.data : []);
      setTotalPages(data?.totalPages ?? 1);
      setTotal(data?.total ?? 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [actionGetBillingHistory]);

  useEffect(() => { load(page); }, [page]);

  return (
    <div className="main-content">
      <style>{skeletonStyle}</style>
      <div className="page-content">
        <div className="container-fluid">

          {/* Header */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-0 main_heading">Billing History</h4>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: 13 }}>
                    Subscription payments and invoices
                  </p>
                </div>
                {!loading && total > 0 && (
                  <span className="badge bg-light text-dark" style={{ fontSize: 13, fontWeight: 600, padding: "6px 14px", borderRadius: 8 }}>
                    {total} record{total !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <div className="card" style={{ borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "none" }}>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ background: "#f8f9fa" }}>
                        <tr>
                          <th className="ps-4 py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Date</th>
                          <th className="py-3"       style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Description</th>
                          <th className="py-3"       style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Amount</th>
                          <th className="py-3"       style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Status</th>
                          <th className="pe-4 py-3"  style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <SkeletonRows />
                        ) : rows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center py-5">
                              <i className="bx bx-receipt" style={{ fontSize: 48, color: "#ced4da" }} />
                              <h6 className="mt-3 text-muted">No billing history yet</h6>
                              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                Payments will appear here once your subscription is active.
                              </p>
                            </td>
                          </tr>
                        ) : (
                          rows.map((row) => (
                            <tr key={row.id}>
                              <td className="ps-4">
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#343a40" }}>
                                  {moment(row.created_at).format("MMM D, YYYY")}
                                </div>
                                <small className="text-muted">
                                  {moment(row.created_at).format("h:mm A")}
                                </small>
                              </td>
                              <td style={{ maxWidth: 320 }}>
                                <div style={{ fontSize: 13, color: "#495057" }}>
                                  {row.description || "Subscription payment"}
                                </div>
                                {row.metadata?.invoice_number && (
                                  <small className="text-muted">#{row.metadata.invoice_number}</small>
                                )}
                              </td>
                              <td>
                                <span style={{ fontWeight: 700, fontSize: 14, color: "#343a40" }}>
                                  ${parseFloat(row.amount ?? 0).toFixed(2)}
                                </span>
                                <small className="text-muted ms-1" style={{ fontSize: 11 }}>
                                  {(row.currency || "USD").toUpperCase()}
                                </small>
                              </td>
                              <td>
                                <StatusBadge status={row.status} />
                              </td>
                              <td className="pe-4">
                                <InvoiceButton row={row} actionGetInvoicePdf={actionGetInvoicePdf} />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {!loading && totalPages > 1 && (
                    <div
                      className="d-flex align-items-center justify-content-between px-4 py-3"
                      style={{ borderTop: "1px solid #f4f5f7" }}
                    >
                      <button
                        className="btn btn-sm btn-light"
                        style={{ borderRadius: 8, fontWeight: 600, minWidth: 90 }}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <i className="bx bx-chevron-left me-1" />Previous
                      </button>
                      <span style={{ fontSize: 13, color: "#6c757d" }}>
                        Page {page} of {totalPages}
                      </span>
                      <button
                        className="btn btn-sm btn-light"
                        style={{ borderRadius: 8, fontWeight: 600, minWidth: 90 }}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next <i className="bx bx-chevron-right ms-1" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, {
  actionGetBillingHistory,
  actionGetInvoicePdf,
})(TransactionHistory);
